import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import artieImage from "../../assets/images/artie.png";
import { Button } from "@/components/ui/button";
import { useProjectStore } from "@/stores/projectStore";
import { apiClient } from "@/lib/api";

const WelcomeBackPage: React.FC = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { project, setProject } = useProjectStore();

  useEffect(() => {
    const fetchAndSetProject = async () => {
      if (projectId && project?._id !== projectId) {
        try {
          const fetchedProject = await apiClient.getProject(projectId);
          setProject(fetchedProject);
        } catch (error) {
          console.error("Failed to fetch project:", error);
          // Optional: redirect to a not-found page or dashboard
          navigate('/dashboard');
        }
      }
    };

    fetchAndSetProject();
  }, [projectId, project, setProject, navigate]);

  useEffect(() => {
    if (project && project.status === "pending" && !project.is_reviewed) {
      navigate(`/initial-assessment?projectId=${project._id}`);
    }
  }, [project, navigate]);

  return (
    <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">
      <main className="relative z-10 w-full flex flex-col items-center min-h-screen">
        <div className="w-full max-w-5xl px-6 py-12 flex flex-col items-center flex-grow">
          {/* Header & Introduction */}
          <div className="flex flex-col items-center text-center max-w-2xl mb-12">
            {/* Logo */}
            <div className="mb-8 relative">
              <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
              <img
                src={artieImage}
                alt="Artie"
                className="w-48 h-48 object-contain relative z-10"
              />
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-cabinet tracking-tight text-[oklch(0.2686_0_0)]">
              Welcome back
            </h1>

            <div className="space-y-1 text-[oklch(0.55_0_0)] text-lg leading-relaxed font-ibm">
              <p>
                Your script has been reviewed by a writer on the Artie team.
              </p>
              <p>
                We've summarized where your project stands against your goal and
                highlighted a few high-impact next steps.
              </p>
            </div>

            <p className="mt-8 text-[oklch(0.55_0_0)] font-ibm">
              You don't need to do everything at once. We'll take this one step
              at a time.
            </p>
          </div>

          {/* The "What happens next" Card */}
          <div className="w-full max-w-3xl bg-gray-50 rounded-2xl p-8 md:p-10 mb-12 border border-gray-200">
            <h2 className="text-lg font-medium text-gray-700 mb-8">
              What happens next
            </h2>

            <div className="relative">
              {/* Vertical Line */}
              <div className="absolute left-[15px] top-4 bottom-4 w-0.5 bg-gray-300"></div>

              {/* Step 1 */}
              <div className="relative flex gap-6 mb-10 last:mb-0">
                <div className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center font-medium text-sm">
                  1
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">
                    Review your script feedback
                  </h3>
                  <p className="text-gray-600">
                    A short summary of what's working and what could be
                    stronger.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative flex gap-6 mb-10 last:mb-0">
                <div className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center font-medium text-sm">
                  2
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">
                    Confirm marketplace readiness
                  </h3>
                  <p className="text-gray-600">
                    We'll review what's needed to make your project ready for
                    brand partners.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative flex gap-6">
                <div className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center font-medium text-sm">
                  3
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">
                    Take a few high-impact next steps
                  </h3>
                  <p className="text-gray-600">
                    Actionable tasks to help put your project in a strong
                    position.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer & Navigation */}
        <div className="w-full mt-auto pb-10 px-20">
          <div className="w-full border-t border-[oklch(0.92_0_0)] mb-8" />
          <div className="w-full flex items-center justify-between">
            <div className="flex-1 flex justify-start">
              <Button
                variant="ghost"
                className="text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:bg-transparent pl-0"
                onClick={() => navigate('/dashboard')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>

            <div className="flex-1 flex justify-end">
              <Button
                onClick={() => navigate(`/project-v2/script-feedback/${project?._id}`)}
                className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!project?._id}
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default WelcomeBackPage;
